/* Next */
import Head from 'next/head'
import { useRouter } from 'next/router'

/* React */
import { useEffect, useMemo, useRef, useState } from 'react'

/* Redux */
import { useSelector, useDispatch } from 'src/redux/hooks'

/* Types */
import { brandsResponse, isBrandsResponse } from 'src/shared/frontendTypes/brand'
import { filterProducts, isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder, isMenuPageBuilderActive } from 'src/shared/dbTypes/menuPageBuilder'
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import type { item as NavigationItem, tMenu } from 'src/shared/frontendTypes/navigation'

/* Functions */
import { post } from 'src/functions/fetch'
import { buildNavigationBreadcrumb } from 'src/functions/navigationBreadcrumbs'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/Filter'
import LandingPage from 'src/pageBuilder/LandingPage'
import useNavigationPaths from 'src/layout/navigationPaths'

/* Options */
import config from 'src/config'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Erweiterte Props-Schnittstelle um die SEO-Felder und canonicalUrl */
interface Props {
  type?: 'menu' | 'brand' | 'brandMenu'
  mid?: number
  bid?: number
  title?: string
  description?: string
  products?: tProductPreview[]
  count?: number
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string
  brandLogo?: string
  stock?: boolean
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  breadcrumb?: BreadcrumbItem[]
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
const Redux = () => {
  const filter = useSelector(store => store.Filter)
  const dispatch = useDispatch()

  const updateFilterProducts = (products: filterProducts) =>
    dispatch({ type: 'updateFilterProducts', payload: products })

  const updateBrands = ({ brands }: brandsResponse) =>
    dispatch({ type: 'updateFilterBrands', payload: brands })

  return { filter, updateFilterProducts, updateBrands, dispatch }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
type NavigationMenuMatch = {
  item: NavigationItem
  parentBrand: NavigationItem | null
}

const findNavigationMenuItem = (
  items: tMenu,
  targetMenuId?: number,
  parentBrand: NavigationItem | null = null,
): NavigationMenuMatch | null => {
  if (!targetMenuId) return null

  for (const item of items) {
    const nextParentBrand = item.brand ? item : parentBrand

    if (item.menueId === targetMenuId) {
      return { item, parentBrand }
    }

    if (item.children?.length) {
      const childMatch = findNavigationMenuItem(item.children, targetMenuId, nextParentBrand)
      if (childMatch) return childMatch
    }
  }

  return null
}

const findNavigationBrandItem = (
  items: tMenu,
  targetBrandId?: number,
  parentBrand: NavigationItem | null = null,
): NavigationMenuMatch | null => {
  if (!targetBrandId) return null

  for (const item of items) {
    const nextParentBrand = item.brand ? item : parentBrand

    if (item.brand === targetBrandId) {
      return { item, parentBrand }
    }

    if (item.children?.length) {
      const childMatch = findNavigationBrandItem(item.children, targetBrandId, nextParentBrand)
      if (childMatch) return childMatch
    }
  }

  return null
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
const FilterPage: React.FC<Props> = ({ 
  type, mid, bid,
  title, description,
  products: ssrProducts,
  count: ssrCount,
  metaTitle, metaDescription, metaKeywords,
  canonicalUrl,
  brandLogo,
  stock,
  pageBuilder,
  sitePageBuilder,
  breadcrumb: propBreadcrumb,
  magazines,
  landingPageImageAiGenerated,
}) => {
  const router = useRouter()
  const navigationPaths = useNavigationPaths()
  const { filter, updateFilterProducts, updateBrands, dispatch } = Redux()
  const builderActive = isMenuPageBuilderActive(pageBuilder)
  const builderChromeActive = builderActive || isMenuPageBuilderActive(sitePageBuilder)
  const [requireStock, setRequireStock] = useState(stock === true)
  const routeKey = useMemo(
    () => [type || '', mid || '', bid || ''].join(':'),
    [type, mid, bid],
  )
  const previousRouteKey = useRef(routeKey)
  const navigationBreadcrumb = buildNavigationBreadcrumb(navigationPaths, { menueId: mid, brandId: bid })
  const fallbackBrandBreadcrumb: BreadcrumbItem[] = type === 'brand' && title?.trim()
    ? [
        { title: 'Marken', href: '/brands' },
        { title, href: canonicalUrl ? `/${canonicalUrl}` : undefined },
      ]
    : []
  const fallbackMenuBreadcrumb: BreadcrumbItem[] = (!type || type === 'menu' || type === 'brandMenu') && title?.trim()
    ? [
        { title, href: canonicalUrl ? `/${canonicalUrl}` : undefined },
      ]
    : []
  const breadcrumb = propBreadcrumb?.length
    ? propBreadcrumb
    : navigationBreadcrumb.length
      ? navigationBreadcrumb
      : fallbackBrandBreadcrumb.length
        ? fallbackBrandBreadcrumb
        : fallbackMenuBreadcrumb
  const navigationMenuMatch = findNavigationMenuItem(navigationPaths, mid)
    || (type === 'brand' ? findNavigationBrandItem(navigationPaths, bid) : null)
  const subcategoryItems = navigationMenuMatch?.item.children || []
  const subcategoryParentBrand = navigationMenuMatch?.parentBrand || null
  const showCatalogFilters = builderChromeActive
    && (!type || type === 'menu' || type === 'brand' || type === 'brandMenu')

  const { 
    size, page, suche, activeFilters, activeBrands,
    products: reduxProducts, 
    count: reduxCount,
    brands,
  } = filter

  const products = reduxProducts !== null 
    ? reduxProducts 
    : ssrProducts || []
  const count = reduxCount !== null 
    ? reduxCount 
    : ssrCount || 0

  useEffect(() => {
    setRequireStock(stock === true)
  }, [stock, mid, bid])

  useEffect(() => {
    if (!router.isReady) return

    const querySuche = router.query.suche
    const nextSuche = typeof querySuche === 'string'
      ? querySuche
      : Array.isArray(querySuche)
        ? querySuche[0] || ''
        : ''

    if (nextSuche === suche) return

    updateFilterProducts({
      products: [],
      count: 0,
      filterTree: [],
      filterBrands: [],
    })

    updateBrands({ count: 0, brands: [] })
    dispatch({ type: 'updateFilterSuche', payload: nextSuche })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.suche])

  const loadBrands = async (signal: AbortSignal) => {
    const data = await post({
      url: 'brands', 
      body: { 
        size: 3, 
        suche, 
        fuzzy: false,
        ...config.shop 
      },
      signal,
    })
    if (!isBrandsResponse(data)) { 
      console.error('Konnte Hersteller nicht laden!', data); 
      return 
    }
    updateBrands(data)
  }

  const loadProducts = async (signal: AbortSignal) => {
    const req: { [k: string]: unknown } = {
      size, page, suche,
      fuzzy: false,
      options: Object.values(activeFilters),
      brands: activeBrands,
      ...config.shop,
      filterTree: true,
      filterBrands: true,
    }

    switch (type) {
      case 'menu':
      case 'brandMenu':
        req.menuId = mid
      case 'brand':
      case 'brandMenu':
        req.brandId = bid
    }

    const data = await post({
      url: 'products', 
      body: req,
      signal,
    })
    if (!isFilterProductsResponse(data)) throw 'Konnte Produkte nicht laden!'
    if (type === 'menu' || type === 'brandMenu') {
      setRequireStock(data.menu?.stock === true)
    }
    updateFilterProducts(data.filterProducts)
  }
  
  useEffect(() => {
    const controller = new AbortController(), signal = controller.signal
    const routeChanged = previousRouteKey.current !== routeKey

    if (routeChanged) {
      previousRouteKey.current = routeKey

      if (page !== 1) {
        updateFilterProducts({
          products: [],
          count: 0,
          filterTree: [],
          filterBrands: [],
        })
        updateBrands({ count: 0, brands: [] })
        dispatch({ type: 'changeFilterPage', payload: 1 })
        return () => controller.abort()
      }
    }

    loadProducts(signal)

    if (!builderActive) {
      if (suche.length >= 3) loadBrands(signal)
      if (suche.length < 3 && brands.length > 0) updateBrands({ count: 0, brands: [] })
    }

    return () => controller.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ 
    size, page, suche, activeFilters, activeBrands,
    mid, bid,
    routeKey,
    builderActive,
  ])

  if (builderActive) return <LandingPage
    {...{
      pageType: type,
      title,
      description,
      metaTitle,
      metaDescription,
      metaKeywords,
      canonicalUrl,
      pageBuilder,
      products,
      count,
      page,
      size,
      requireStock,
      breadcrumb,
      subcategoryItems,
      subcategoryParentBrand,
      showCatalogFilters,
      magazines,
      landingPageImageAiGenerated,
      brandLogo,
    }}
  />

  return <>
    <Head>
      <title>Suche</title>
    </Head>

    <Filter {...{
      pageType: type,
      products, page, size, count,
      brands,
      title, description,
      metaTitle, metaDescription, metaKeywords,
      canonicalUrl, // Hier wird die fertig gebaute URL weitergegeben
      brandLogo,
      requireStock,
      breadcrumb,
      subcategoryItems,
      subcategoryParentBrand,
      showCatalogFilters,
      sitePageBuilder,
      magazines,
      landingPageImageAiGenerated,
    }}/>
  </>
}

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
